/**
 * Най-големият шрифт (px) ≤ maxPx, при който `text` се събира в `availableWidthPx` на 1 ред
 * (при многоредово — най-дългият ред). Един и същ резултат навсякъде (едитор + всички превюта).
 *
 * SSR-safe: без `document` връща `maxPx` (клиентът прецизира в useLayoutEffect, без мигане).
 *
 * МЕРЕНЕТО Е С CANVAS, не с DOM. Старата версия слагаше `<span>` в `document.body` и четеше
 * `offsetWidth` — всяко такова четене форсира преизчисление на layout-а на ЦЯЛАТА страница.
 * Началната страница мери ~30 корици × 2 (заглавие + година) и повтаря всичко на `fonts.ready`
 * → ~120 forced reflow-а, 294 ms в одита от 2026-08-29.
 *
 * Съвместимо ли е? Функцията мери само `fontFamily`/`fontWeight`/`fontSize` — `letter-spacing`
 * и `text-transform` никога не са участвали, а глобалните стилове не задават нито едното.
 * (`BookCover.jsx` в едитора мери С тях от computed style и затова НЕ е мигриран — canvas не
 * поддържа надеждно letter-spacing, а там се мери една корица, не трийсет.)
 *
 * @param {{ text: string, fontFamily?: string, fontWeight?: string|number,
 *   maxPx: number, minPx?: number, availableWidthPx: number }} opts
 */

/** Ширината на текста е линейна спрямо размера → мери веднъж на еталон и мащабирай. */
const REFERENCE_FONT_SIZE_PX = 100;

/** `${weight}|${family}|${text}` → ширина в px при размер 1px. */
const widthPerPxCache = new Map();

let measureContext;
let fontListenerAttached = false;

/**
 * Един canvas за целия живот на страницата — създаването му е евтино, но не безплатно, а
 * началната страница мери десетки пъти.
 */
function getMeasureContext() {
  if (measureContext !== undefined) {
    return measureContext;
  }
  try {
    measureContext = document.createElement('canvas').getContext('2d') || null;
  } catch {
    measureContext = null;
  }
  return measureContext;
}

/**
 * Първата мярка може да е с fallback шрифт (Oddval още се сваля) — тогава кешът пази грешна
 * ширина. `loadingdone` се вика при всяко зареждане на шрифт, включително динамичните в едитора.
 */
function attachFontInvalidation() {
  if (fontListenerAttached || typeof document === 'undefined' || !document.fonts) {
    return;
  }
  fontListenerAttached = true;
  try {
    document.fonts.addEventListener('loadingdone', () => widthPerPxCache.clear());
  } catch {
    /* по-стари браузъри без FontFaceSet events — кешът просто не се чисти */
  }
}

/** Ширина на един ред при размер 1px. 0 = не може да се измери. */
function measureWidthPerPx(text, fontFamily, fontWeight) {
  const family = fontFamily || 'Arial, sans-serif';
  const weight = String(fontWeight || '700');
  const key = `${weight}|${family}|${text}`;

  const cached = widthPerPxCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const ctx = getMeasureContext();
  if (!ctx) {
    return 0;
  }

  attachFontInvalidation();
  ctx.font = `${weight} ${REFERENCE_FONT_SIZE_PX}px ${family}`;
  const widthPerPx = ctx.measureText(text).width / REFERENCE_FONT_SIZE_PX;

  if (widthPerPx > 0) {
    widthPerPxCache.set(key, widthPerPx);
  }
  return widthPerPx;
}

export function fitTitleFontSizePx({
  text,
  fontFamily,
  fontWeight = '700',
  maxPx,
  minPx = 1,
  availableWidthPx,
}) {
  if (typeof document === 'undefined' || !text || !(maxPx > 0) || !(availableWidthPx > 0)) {
    return maxPx;
  }

  // `.trim()` НЕ е козметика: реални заглавия са "DOMINICAN \nREPUBLIC", "CHRISTMAS \nMARKET"
  // — с интервал в края на реда. CSS не рисува интервал в края на ред, но `measureText` го
  // брои, така че без trim тези заглавия щяха да се свиват излишно спрямо старото поведение.
  const longest = String(text)
    .split('\n')
    .map((line) => line.trim())
    .reduce((a, b) => (b.length > a.length ? b : a), '');

  const widthPerPx = measureWidthPerPx(longest || String(text).trim(), fontFamily, fontWeight);
  if (!(widthPerPx > 0)) {
    return maxPx;
  }

  // Размерът, при който текстът точно запълва наличната ширина. Линейността прави цикъла излишен.
  const fitted = availableWidthPx / widthPerPx;
  return Math.max(minPx, Math.min(maxPx, fitted));
}

/** Само за тестове — кешът е модулен и преживява рендерите. */
export function __clearMeasureCacheForTests() {
  widthPerPxCache.clear();
  measureContext = undefined;
  fontListenerAttached = false;
}
